import { Grid, makeStyles } from "@material-ui/core";
import { IBillingData } from "api/interfaces/megaERP";
import { DialogModalWithSteps } from "components/UI/DialogModals";
import { PageLoading } from "components/UI/Page";
import TabPanel from "components/UI/Tabs/TabPanel";
import { lang } from "i18nLang";
import { useEffect, useState } from "react";
import { MeasurementListStore, MeasurementStore } from "store";
import { formatNumberToMonetary } from "utils";
import MeasurementDetails from "./MeasurementDetails";
import ResumeItems from "./ResumeItems";

const useStyles = makeStyles({
  dialog: {
    "& .finish-button": {
      display: "none",
    },
  },
  loading: { padding: "3rem 0" },
});

/** Componente modal para visualizar os dados enviados para
 * faturamento da medição informada.
 */
const ShowBillingData = ({
  open,
  onClose,
  measurementId,
}: {
  /** Sinaliza se a modal deve ser exibida ou não. */
  open: boolean;
  /** Evento disparado ao fechar a modal. */
  onClose: () => void;
  /** ID da medição. */
  measurementId: string;
}) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [billingData, setBillingData] = useState<IBillingData | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(false);
  const steps = [
    {
      title: lang("billing.steps.client"),
    },
    { title: lang("billing.steps.measurement") },
    { title: lang("billing.steps.resume") },
  ];

  useEffect(() => {
    const measurement = MeasurementListStore.findMeasurement(measurementId);
    if (!measurement) return;

    setLoading(true);
    MeasurementStore.getMeasurementBillingData(measurement).then((data) => {
      setBillingData(data);
      setLoading(false);
    });
  }, [measurementId]);

  const handleNext = (currentStep: number, nextStep: number) => {
    setActiveStep(nextStep);
  };

  return (
    <DialogModalWithSteps
      open={open}
      onClose={onClose}
      title={lang("billing.salesDocument")}
      className={classes.dialog}
      maxWidth={"lg"}
      steps={steps}
      activeStep={activeStep}
      onFinish={() => {}}
      onNext={handleNext}
      cancelLabel={lang("close")}
      nextLabel={lang("next")}
    >
      {loading && <PageLoading className={classes.loading} />}
      {!loading && (
        <>
          <TabPanel value={activeStep} index={0}>
            <Grid container>
              <MeasurementDetails
                title={lang("fields.branch")}
                value={billingData?.branch.name || "-"}
              />
              <MeasurementDetails
                title={lang("fields.client")}
                value={billingData?.client.name || "-"}
              />
              <MeasurementDetails
                title={lang("fields.projectErp")}
                value={billingData?.project.name || "-"}
              />
              <MeasurementDetails
                title={lang("fields.costCenter")}
                value={billingData?.costCenter.name || "-"}
              />
              <MeasurementDetails
                title={lang("fields.paymentCondition")}
                value={billingData?.paymentCondition.name || "-"}
              />
              <MeasurementDetails
                title={lang("fields.documentType")}
                value={billingData?.documentType.name || "-"}
              />
            </Grid>
          </TabPanel>
          <TabPanel value={activeStep} index={1}>
            <Grid container>
              <MeasurementDetails
                title={lang("billing.fields.totalMeasured")}
                value={formatNumberToMonetary(
                  billingData?.measurementTotal || 0
                )}
              />
              <MeasurementDetails
                title={lang("billing.fields.billing")}
                value={formatNumberToMonetary(
                  billingData?.measurementBillingsTotal || 0
                )}
              />
              <MeasurementDetails
                title={lang("billing.fields.totalReceivable")}
                value={formatNumberToMonetary(
                  billingData?.measurementNetTotal || 0
                )}
              />
            </Grid>
          </TabPanel>
          <TabPanel value={activeStep} index={2}>
            <ResumeItems rowsData={billingData?.items || []} />
          </TabPanel>
        </>
      )}
    </DialogModalWithSteps>
  );
};

export default ShowBillingData;
